import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  useAppStore,
  KeyframeProp,
  emptyLayerKeyframes,
} from '../store/useAppStore';
import { viewportActions } from '../viewportActions';
import { RULER_H, LABEL_W, ROW_H, SUB_H, ALL_PROPS, CAM_PROPS, kfKey, SelectedLayerKf } from './timeline/timelineConstants';
import { RowDescriptor } from '../hooks/useKfSelection';
import { usePlayback }         from '../hooks/usePlayback';
import { useLayerKfDrag }      from '../hooks/useLayerKfDrag';
import { useCameraKfDrag }     from '../hooks/useCameraKfDrag';
import { useLayerBarDrag }     from '../hooks/useLayerBarDrag';
import { useTimelineHelpers }  from '../hooks/useTimelineHelpers';
import { useKfSelection }      from '../hooks/useKfSelection';
import { useTimelineKeyboard } from '../hooks/useTimelineKeyboard';
import TimelineRuler           from './timeline/TimelineRuler';
import KeyframeEasingMenu      from './timeline/KeyframeEasingMenu';
import { LayerLabelRow, LayerTrackRow }  from './timeline/LayerTrack';
import { CameraLabelRow, CameraTrackRow } from './timeline/CameraTrack';
import TimelineControlsBar    from './timeline/TimelineControlsBar';
import { getLayerDepth }      from '../utils/viewportMathUtils';


// ── Component ─────────────────────────────────────────────────────────────────
const Timeline: React.FC = () => {
  const layers             = useAppStore((s) => s.layers);
  const selectedId         = useAppStore((s) => s.selectedLayerId);
  const selectedIds        = useAppStore((s) => s.selectedLayerIds);
  const selectLayer        = useAppStore((s) => s.selectLayer);
  const selectLayers       = useAppStore((s) => s.selectLayers);
  const selectLayerRange   = useAppStore((s) => s.selectLayerRange);
  const addToSelection     = useAppStore((s) => s.addToSelection);
  const removeFromSelection = useAppStore((s) => s.removeFromSelection);
  const toggleSelection    = useAppStore((s) => s.toggleSelection);
  const setLayersOrder     = useAppStore((s) => s.setLayersOrder);
  const totalFrames        = useAppStore((s) => s.totalFrames);
  const fps                = useAppStore((s) => s.fps);
  const currentFrame       = useAppStore((s) => s.currentFrame);
  const isPlaying          = useAppStore((s) => s.isPlaying);
  const cameraTrackEnabled = useAppStore((s) => s.cameraTrackEnabled);
  const cameraKeyframes    = useAppStore((s) => s.cameraKeyframes);
  const inPoint            = useAppStore((s) => s.inPoint);
  const outPoint           = useAppStore((s) => s.outPoint);
  const setKeyframeEasing  = useAppStore((s) => s.setKeyframeEasing);
  const setCurrentFrame    = useAppStore((s) => s.setCurrentFrame);

  const [zoom, setZoom]             = useState(1);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [trackAreaW, setTrackAreaW] = useState(600);
  const [easingMenu, setEasingMenu] = useState<{
    x: number; y: number;
    layerId: string; prop: KeyframeProp | 'summary'; frame: number;
  } | null>(null);
  const [camExpanded, setCamExpanded] = useState(false);
  const [dropState, setDropState] = useState<{ mode: 'reorder'; index: number } | { mode: 'parent'; targetId: string } | null>(null);

  const trackScrollRef = useRef<HTMLDivElement>(null);
  const labelScrollRef = useRef<HTMLDivElement>(null);
  const lastSelectedIdRef = useRef<string | null>(null);

  const basePx     = trackAreaW / totalFrames;
  const pxPerFrame = basePx * zoom;
  const totalPx    = Math.max(totalFrames * pxPerFrame, trackAreaW);

  // ── Hooks ─────────────────────────────────────────────────────────────────
  usePlayback(trackScrollRef, pxPerFrame);

  // Refs shared between useKfSelection and useLayerKfDrag so that kf drag
  // can move the entire selection without needing to re-order hook calls.
  const selectedKfsRef    = useRef<SelectedLayerKf[]>([]);
  const setSelectedKfsRef = useRef<(kfs: SelectedLayerKf[]) => void>(() => {});

  const { dragState, kfDragMovedRef, handleKfPointerDown, handleKfPointerMove, handleKfPointerUp } =
    useLayerKfDrag({ trackScrollRef, pxPerFrame, totalFrames, selectedKfsRef, setSelectedKfs: (kfs) => setSelectedKfsRef.current(kfs) });

  const { camDragState, camDragMovedRef, handleCamKfPointerDown, handleCamKfPointerMove, handleCamKfPointerUp } =
    useCameraKfDrag({ trackScrollRef, pxPerFrame, totalFrames });

  const { barDragState, dragJustCompletedRef, handleBarPointerDown, handleBarPointerMove, handleBarPointerUp } =
    useLayerBarDrag({ pxPerFrame, totalFrames });

  const {
    expanded, toggleExpanded,
    addAllKfsAtFrame, removeAllKfsAtFrame, addPropKfAtFrame, hasKfAtFrame,
    addCamKfAtFrame, removeCamKfAtFrame, removeKeyframe,
  } = useTimelineHelpers();

  // ── Row layout for cross-layer region selection ───────────────────────────
  // Computes the Y offset of every visible track row so useKfSelection can
  // determine which layers / properties a dragged region rectangle covers.
  const rowLayout = useMemo((): RowDescriptor[] => {
    const rows: RowDescriptor[] = [];
    let y = 0;
    // Skip camera rows — camera kfs use their own data structure.
    if (cameraTrackEnabled) {
      y += ROW_H;
      if (camExpanded) y += CAM_PROPS.length * SUB_H;
    }
    for (const layer of layers) {
      rows.push({ layerId: layer.id, prop: null, top: y, height: ROW_H });
      y += ROW_H;
      if (expanded.has(layer.id)) {
        for (const prop of ALL_PROPS) {
          rows.push({ layerId: layer.id, prop: prop as KeyframeProp, top: y, height: SUB_H });
          y += SUB_H;
        }
      }
    }
    return rows;
  }, [cameraTrackEnabled, camExpanded, layers, expanded]);
  const rowLayoutRef = useRef<RowDescriptor[]>([]);
  rowLayoutRef.current = rowLayout;

  const {
    selectedKfs, setSelectedKfs, regionSelect, kfClipboardRef, frameFromTrack,
    clearSelectedKfs, copySelectedKfs, pasteSelectedKfsAtFrame, deleteSelectedKfs,
    handleRegionPointerDown,
    handleRemoveAllKfsAndDeselect, handleSummaryKfClick, handlePropKfClick, handleRemovePropKfWithSnapshot,
  } = useKfSelection({ trackScrollRef, pxPerFrame, totalFrames, removeAllKfsAtFrame, rowLayoutRef });

  // Keep shared refs in sync so useLayerKfDrag always sees the latest state.
  selectedKfsRef.current    = selectedKfs;
  setSelectedKfsRef.current = setSelectedKfs;
  viewportActions.selectedKfsCount.current = selectedKfs.length;

  useTimelineKeyboard({
    selectedKfs, kfClipboardRef, copySelectedKfs,
    pasteSelectedKfsAtFrame, deleteSelectedKfs, clearSelectedKfs,
  });

  // ── Multi-select layer handling ──────────────────────────────────────────────
  const handleLayerLabelClick = useCallback((layerId: string, e: React.MouseEvent) => {
    e.preventDefault();
    const isCmdKey = e.ctrlKey || e.metaKey;
    const isShiftKey = e.shiftKey;

    if (isShiftKey && lastSelectedIdRef.current) {
      // Shift-click: select range
      selectLayerRange(lastSelectedIdRef.current, layerId);
    } else if (isCmdKey) {
      // Ctrl/Cmd-click: toggle
      toggleSelection(layerId);
    } else {
      // Regular click: single select
      selectLayer(layerId);
    }
    lastSelectedIdRef.current = layerId;
  }, [selectLayer, toggleSelection, selectLayerRange]);

  // ── Drag-reorder layers ──────────────────────────────────────────────────────
  const computeDropZone = useCallback((clientY: number): { mode: 'reorder'; index: number } | { mode: 'parent'; targetId: string } | null => {
    if (!labelScrollRef.current) return { mode: 'reorder', index: 0 };
    const rows = Array.from(labelScrollRef.current.querySelectorAll<HTMLElement>('[data-layer-id]'));
    for (let i = 0; i < rows.length; i++) {
      const rect = rows[i].getBoundingClientRect();
      if (clientY > rect.bottom) continue;
      const relY = clientY - rect.top;
      const rowH = rect.height;
      if (relY < rowH * 0.25) {
        return { mode: 'reorder', index: i };
      } else if (relY < rowH * 0.75) {
        const id = rows[i].getAttribute('data-layer-id');
        if (id) return { mode: 'parent', targetId: id };
        return { mode: 'reorder', index: i };
      } else {
        return { mode: 'reorder', index: i + 1 };
      }
    }
    return { mode: 'reorder', index: rows.length };
  }, []);

  const handleLabelDragOver = useCallback((evt: React.DragEvent) => {
    evt.preventDefault();
    evt.dataTransfer.dropEffect = 'move';
    setDropState(computeDropZone(evt.clientY));
  }, [computeDropZone]);

  const handleLabelDrop = useCallback((evt: React.DragEvent) => {
    evt.preventDefault();
    const draggedId = evt.dataTransfer.getData('text/plain');
    const ds = dropState;
    setDropState(null);
    if (!draggedId || !ds) return;
    const { layers: ls } = useAppStore.getState();
    if (ds.mode === 'reorder') {
      const draggedLayer = ls.find((l) => l.id === draggedId);
      if (!draggedLayer) return;
      
      // Determine which layers to move (all selected or just dragged)
      const layersToMove = selectedIds.includes(draggedId) ? selectedIds : [draggedId];
      
      // Get indices of all layers to move
      const moveFromIndices = layersToMove
        .map((id) => ls.findIndex((l) => l.id === id))
        .filter((idx) => idx >= 0)
        .sort((a, b) => a - b);
      
      if (moveFromIndices.length === 0) return;
      
      // Get display order before and after
      const lowestFromIdx = moveFromIndices[0];
      const highestFromIdx = moveFromIndices[moveFromIndices.length - 1];
      
      // Calculate target index in final list (adjust for removed items)
      let targetIdx = ds.index;
      if (lowestFromIdx < ds.index) {
        targetIdx -= moveFromIndices.length;
      }
      
      if (lowestFromIdx === targetIdx) return; // No change
      
      // Build new order
      const newLayers = ls.filter((l) => !layersToMove.includes(l.id));
      const movedLayers = ls.filter((l) => layersToMove.includes(l.id));
      newLayers.splice(targetIdx, 0, ...movedLayers);
      
      viewportActions.pushLayerSnapshot.current();
      setLayersOrder(newLayers);
    }
  }, [dropState, selectedIds, setLayersOrder]);

  const handleLabelDragLeave = useCallback((evt: React.DragEvent) => {
    if (!labelScrollRef.current?.contains(evt.relatedTarget as Node)) {
      setDropState(null);
    }
  }, []);

  // ── Tools menu actions ───────────────────────────────────────────────────────────────

  /** Remove every keyframe on every layer (undo-able). */
  const handleClearKeyframes = useCallback(() => {
    viewportActions.pushSnapshot.current();
    const freshKfs: Record<string, ReturnType<typeof emptyLayerKeyframes>> = {};
    for (const layer of useAppStore.getState().layers) {
      freshKfs[layer.id] = emptyLayerKeyframes();
    }
    useAppStore.setState({ layerKeyframes: freshKfs });
    clearSelectedKfs();
  }, [clearSelectedKfs]);

  /**
   * Set fade-in opacity keyframes (0 → 1 over 5 frames) on every layer,
   * evenly distributed across the total timeline duration so each layer
   * is visible for the same amount of time before the next one fades in.
   */
  const handleRippleOpacity = useCallback(() => {
    const { layers: ls, totalFrames: tf } = useAppStore.getState();
    const N = ls.length;
    if (!N) return;
    viewportActions.pushSnapshot.current();
    // Divide the timeline evenly among layers (bottom of stack first).
    // Each layer's timing bar is set to its slice; no keyframes are written.
    const sliceSize = tf / N;
    useAppStore.setState((s) => ({
      layers: s.layers.map((layer) => {
        const stackIdx = N - 1 - s.layers.indexOf(layer); // 0 = bottom
        const start = Math.round(stackIdx * sliceSize);
        // const end   = Math.min(Math.round((stackIdx + 1) * sliceSize) - 1, tf - 1);
        const end = tf - 1;
        return { ...layer, frameStart: start, frameEnd: end };
      }),
    }));
  }, []);

  const selectedKeySet = useMemo(
    () => new Set(selectedKfs.map((k) => kfKey(k.layerId, k.prop, k.frame))),
    [selectedKfs],
  );

  // ── Track area width observer ─────────────────────────────────────────────
  useEffect(() => {
    const el = trackScrollRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) setTrackAreaW(e.contentRect.width);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // ── Close easing menu on outside click ───────────────────────────────────
  useEffect(() => {
    if (!easingMenu) return;
    const close = (e: PointerEvent) => {
      if (!(e.target as HTMLElement)?.closest?.('[data-easing-menu]')) setEasingMenu(null);
    };
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
  }, [easingMenu]);

  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const sl = e.currentTarget.scrollLeft;
    setScrollLeft(sl);
    if (labelScrollRef.current) labelScrollRef.current.scrollTop = e.currentTarget.scrollTop;
  }, []);

  // ── Render ────────────────────────────────────────────────────────────────
  const displayFrame = Math.round(currentFrame);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#12121f', color: '#ccc', userSelect: 'none', overflow: 'hidden' }}>

      {/* ── Controls bar ── */}
      <TimelineControlsBar
        zoom={zoom}
        onZoomChange={setZoom}
        selectedKfs={selectedKfs}
        onSetKeyframeEasing={setKeyframeEasing}
        onClearKeyframes={handleClearKeyframes}
        onRippleOpacity={handleRippleOpacity}
      />


      {/* ── Ruler + track body ── */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

        {/* ── Label column (fixed, non-scrolling horizontally) ── */}
        <div style={{ width: LABEL_W, flexShrink: 0, display: 'flex', flexDirection: 'column', borderRight: '1px solid #2a2a40', background: '#0d0d1a' }}>
          {/* Ruler row spacer */}
          <div style={{ height: RULER_H, flexShrink: 0, borderBottom: '1px solid #2a2a40' }} />
          {/* Layer label rows, synced vertically with track scroll */}
          <div
            ref={labelScrollRef}
            style={{ flex: 1, overflowY: 'hidden', overflowX: 'hidden' }}
            onDragOver={handleLabelDragOver}
            onDrop={handleLabelDrop}
            onDragLeave={handleLabelDragLeave}
          >
            {/* ── Camera track label ── */}
            {cameraTrackEnabled && (
              <CameraLabelRow
                camExpanded={camExpanded}
                displayFrame={displayFrame}
                cameraKeyframes={cameraKeyframes}
                onToggleExpanded={() => setCamExpanded((v) => !v)}
                onAddCamKf={addCamKfAtFrame}
                onRemoveCamKf={removeCamKfAtFrame}
              />
            )}
            {layers.map((layer, idx) => (
              <React.Fragment key={layer.id}>
                {dropState?.mode === 'reorder' && dropState.index === idx && (
                  <div style={{ height: 2, margin: '-1px 0', background: '#1890ff', borderRadius: 1, pointerEvents: 'none' }} />
                )}
                <div
                  data-layer-id={layer.id}
                  draggable
                  onDragStart={(evt) => {
                    evt.dataTransfer.setData('text/plain', layer.id);
                    evt.dataTransfer.effectAllowed = 'move';
                  }}
                  onClick={(e) => handleLayerLabelClick(layer.id, e)}
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                >
                  <LayerLabelRow
                    layer={layer}
                    isActive={selectedIds.includes(layer.id)}
                    isExpanded={expanded.has(layer.id)}
                    displayFrame={displayFrame}
                    indentLevel={getLayerDepth(layer.id, layers)}
                    onToggleExpanded={toggleExpanded}
                    onAddAllKfs={addAllKfsAtFrame}
                    onRemoveAllKfs={removeAllKfsAtFrame}
                    onAddPropKf={addPropKfAtFrame}
                    onRemoveKf={removeKeyframe}
                  />
                </div>
                {dropState?.mode === 'reorder' && dropState.index === layers.length && idx === layers.length - 1 && (
                  <div style={{ height: 2, margin: '-1px 0', background: '#1890ff', borderRadius: 1, pointerEvents: 'none' }} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* ── Track content column ── */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Ruler */}
          <TimelineRuler
            trackAreaW={trackAreaW}
            pxPerFrame={pxPerFrame}
            scrollLeft={scrollLeft}
            totalFrames={totalFrames}
            currentFrame={currentFrame}
            fps={fps}
            inPoint={inPoint}
            outPoint={outPoint}
            isPlaying={isPlaying}
          />

          {/* Scrollable track rows */}
          <div ref={trackScrollRef}
            className="dark-scroll"
            style={{ flex: 1, overflowX: 'auto', overflowY: 'auto' }}
            onScroll={handleScroll}
          >
            <div style={{ width: totalPx, position: 'relative' }}
              onPointerDown={handleRegionPointerDown}
            >

              {/* Global cross-layer region selection box */}
              {regionSelect && (
                <div style={{
                  position: 'absolute',
                  pointerEvents: 'none',
                  zIndex: 2,
                  left:  Math.min(regionSelect.startFrame, regionSelect.endFrame) * pxPerFrame,
                  width: Math.max(2, (Math.abs(regionSelect.endFrame - regionSelect.startFrame) + 1) * pxPerFrame),
                  top:    Math.min(regionSelect.startY, regionSelect.endY),
                  height: Math.max(2, Math.abs(regionSelect.endY - regionSelect.startY)),
                  background: 'rgba(88,166,255,0.12)',
                  border: '1px solid rgba(88,166,255,0.6)',
                }} />
              )}

              {/* In/out region shading in track area */}
              {(inPoint !== null || outPoint !== null) && (
                <div style={{
                  position: 'absolute', top: 0, bottom: 0, zIndex: 1, pointerEvents: 'none',
                  left: (inPoint ?? 0) * pxPerFrame,
                  width: ((outPoint ?? totalFrames - 1) - (inPoint ?? 0) + 1) * pxPerFrame,
                  background: 'rgba(255,255,255,0.025)',
                  borderLeft:  inPoint  !== null ? '2px solid rgba(34,197,94,0.45)'  : undefined,
                  borderRight: outPoint !== null ? '2px solid rgba(248,113,113,0.45)' : undefined,
                }} />
              )}

              {/* Playhead needle across all rows */}
              <div style={{
                position: 'absolute', top: 0, bottom: 0,
                left: Math.round(currentFrame) * pxPerFrame,
                width: 2, background: '#f5a623', opacity: 0.9,
                transform: 'translateX(-1px)', zIndex: 10, pointerEvents: 'none',
              }} />

              {/* ── Camera track rows ── */}
              {cameraTrackEnabled && (
                <CameraTrackRow
                  camExpanded={camExpanded}
                  cameraKeyframes={cameraKeyframes}
                  camDragState={camDragState}
                  pxPerFrame={pxPerFrame}
                  scrollLeft={scrollLeft}
                  trackAreaW={trackAreaW}
                  onSetCurrentFrame={setCurrentFrame}
                  onAddCamKf={addCamKfAtFrame}
                  onRemoveCamKf={removeCamKfAtFrame}
                  onCamKfPointerDown={handleCamKfPointerDown}
                  onCamKfPointerMove={handleCamKfPointerMove}
                  onCamKfPointerUp={handleCamKfPointerUp}
                  frameFromTrack={frameFromTrack}
                  camDragMovedRef={camDragMovedRef}
                />
              )}
              {layers.map((layer) => {
                const isExp  = expanded.has(layer.id);
                const isActive = selectedIds.includes(layer.id);
                const handleTrackRowSelectLayer = (e: React.MouseEvent) => {
                  // Don't change selection if we just completed a drag
                  if (dragJustCompletedRef.current) return;
                  const isCmdKey = e.ctrlKey || e.metaKey;
                  const isShiftKey = e.shiftKey;
                  if (isShiftKey && lastSelectedIdRef.current) {
                    selectLayerRange(lastSelectedIdRef.current, layer.id);
                  } else if (isCmdKey) {
                    toggleSelection(layer.id);
                  } else {
                    selectLayer(layer.id);
                  }
                  lastSelectedIdRef.current = layer.id;
                };
                return (
                  <LayerTrackRow
                    key={layer.id}
                    layer={layer}
                    isActive={isActive}
                    isExpanded={isExp}
                    selectedLayerIds={selectedIds}
                    pxPerFrame={pxPerFrame}
                    scrollLeft={scrollLeft}
                    trackAreaW={trackAreaW}
                    totalFrames={totalFrames}
                    barDragState={barDragState}
                    dragState={dragState}
                    selectedKeySet={selectedKeySet}
                    onBarPointerDown={handleBarPointerDown}
                    onBarPointerMove={handleBarPointerMove}
                    onBarPointerUp={handleBarPointerUp}
                    onSelectLayer={handleTrackRowSelectLayer}
                    onKfPointerDown={handleKfPointerDown}
                    onKfPointerMove={handleKfPointerMove}
                    onKfPointerUp={handleKfPointerUp}
                    onSetCurrentFrame={setCurrentFrame}
                    onAddAllKfs={addAllKfsAtFrame}
                    onAddPropKf={addPropKfAtFrame}
                    onRemoveAllKfsAndDeselect={handleRemoveAllKfsAndDeselect}
                    onRemovePropKfWithSnapshot={handleRemovePropKfWithSnapshot}
                    onOpenEasingMenu={setEasingMenu}
                    onSummaryKfClick={handleSummaryKfClick}
                    onPropKfClick={handlePropKfClick}
                    frameFromTrack={frameFromTrack}
                    kfDragMovedRef={kfDragMovedRef}
                  />
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* ── Easing context menu ── */}
      {easingMenu && (
        <KeyframeEasingMenu
          menu={easingMenu}
          onClose={() => setEasingMenu(null)}
        />
      )}
    </div>
  );
};

export default Timeline;
